// ═══════════════════════════════════════════════════════════════
// Floor session — where a shop tablet was standing, and who held it
// ═══════════════════════════════════════════════════════════════
// The five Galaxy Tab Active5 kiosks live on one TABS department screen
// for a whole shift. Two things used to throw them off it:
//
//   • the idle auto-update (utils/versionCheck) reloads the tab after a
//     deploy. The refresh-restore kit brings the fresh build back to the
//     JOB and its TABS pane — but not to the department sub-tab, not to
//     the full-screen operator screen, and not signed in as anyone. The
//     operator came back to a desktop parts list and had to click their
//     way down four dense screens to get back to work.
//   • the operator name lived in React state, so any reload (a swipe, a
//     crash, a low battery) forgot who was casting.
//
// This is the one record of that state. TabsModule reads the sub-tab from
// it on mount; each department screen reads its operator from it; both
// write back on change. It is preserved across the clean-first-run reset
// in main.tsx (that reset is exactly what runs on an update reload), and
// the operator name expires after a shift so tomorrow's first tap on a
// shared tablet asks "who's casting?" again.
//
// Since 2026-10-14 a department tab IS its operator screen (Drew: "we don't
// need two views"), so the sub-tab alone says which screen comes up; `floor`
// is written true while one is up and false by `leaveFloor`, as the record
// of where the tablet stood. Nothing decides a view from it any more.
// ═══════════════════════════════════════════════════════════════

export const FLOOR_SESSION_KEY = 'mos:floor-session';

/** A signed-in name is trusted for one shift, then the gate asks again. */
export const FLOOR_OPERATOR_TTL_MS = 12 * 60 * 60_000;

export interface FloorSession {
  /** The job whose TABS module the tablet is parked on. */
  jobId: string;
  /** Active TABS sub-tab ('production', 'palletizing', … or a desk tab). */
  tab: string;
  /** True while a department's full-screen operator screen is up. */
  floor: boolean;
  /** Who signed in on the floor screen. */
  operator: string | null;
  /** ISO timestamp of the last write — the operator's TTL counts from here. */
  at: string;
  /**
   * What each department screen had picked, by sub-tab: Casting's part
   * (1.2), Packaging's pallet (#58). An update reload used to drop Casting
   * back onto the job's first part and Packaging onto its first building
   * pallet (Drew, 2026-10-16); the fresh build reopens on these instead.
   */
  picks: Record<string, string>;
}

function readPicks(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [tab, id] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof id === 'string' && id) out[tab] = id;
  }
  return out;
}

function readRaw(): FloorSession | null {
  try {
    const raw = localStorage.getItem(FLOOR_SESSION_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object' || typeof p.jobId !== 'string' || typeof p.tab !== 'string') return null;
    return {
      jobId: p.jobId,
      tab: p.tab,
      floor: p.floor === true,
      operator: typeof p.operator === 'string' && p.operator.trim() ? p.operator : null,
      at: typeof p.at === 'string' ? p.at : '',
      picks: readPicks(p.picks),
    };
  } catch {
    return null;
  }
}

/** The stored session, with an expired operator already dropped. */
export function loadFloorSession(): FloorSession | null {
  const s = readRaw();
  if (!s) return null;
  const stamp = Date.parse(s.at);
  const expired = !Number.isFinite(stamp) || Date.now() - stamp > FLOOR_OPERATOR_TTL_MS;
  return expired ? { ...s, operator: null } : s;
}

/**
 * Merge a patch into the stored session. A patch for a DIFFERENT job
 * replaces the record outright — the tablet moved to another job, and the
 * old operator/floor flags mean nothing there.
 */
export function saveFloorSession(patch: Partial<FloorSession> & { jobId: string }): void {
  const prev = readRaw();
  const base: FloorSession = prev && prev.jobId === patch.jobId
    ? prev
    : { jobId: patch.jobId, tab: 'parts', floor: false, operator: null, at: '', picks: {} };
  const next: FloorSession = { ...base, ...patch, at: new Date().toISOString() };
  try { localStorage.setItem(FLOOR_SESSION_KEY, JSON.stringify(next)); } catch { /* storage disabled */ }
}

export function clearFloorSession(): void {
  try { localStorage.removeItem(FLOOR_SESSION_KEY); } catch { /* ignore */ }
}

/**
 * A department screen's way out (Back to Plant): the next visit to this
 * job's TABS opens on the parts list, not straight back onto the screen
 * just left. The operator stays signed in for the rest of the shift.
 */
export function leaveFloor(jobId: string, operator: string | null): void {
  saveFloorSession({ jobId, tab: 'parts', floor: false, operator });
}

/**
 * What a department screen should wake up as: who was signed in. `floor`
 * is only honoured when the stored sub-tab IS this department (kept for the
 * record and the drives; no screen decides a view from it any more).
 */
export function floorSeed(jobId: string, tab: string): { floor: boolean; operator: string | null } {
  const s = loadFloorSession();
  if (!s || s.jobId !== jobId) return { floor: false, operator: null };
  return { floor: s.floor && s.tab === tab, operator: s.operator };
}

/** The sub-tab to open a job's TABS module on, if this tablet was parked there. */
export function floorTabFor(jobId: string): string | null {
  const s = loadFloorSession();
  return s && s.jobId === jobId ? s.tab : null;
}

/**
 * What a department screen had picked on this job (Casting's part,
 * Packaging's pallet), if it is still one the screen can offer — what the
 * screen wakes up on after an update reload.
 */
export function floorPickFor(jobId: string, tab: string, offered: string[]): string | null {
  const s = readRaw();
  if (!s || s.jobId !== jobId) return null;
  const id = s.picks[tab];
  return id && offered.includes(id) ? id : null;
}

/** Remember what a department screen has picked, for the next reload. */
export function saveFloorPick(jobId: string, tab: string, id: string): void {
  const prev = readRaw();
  const picks = prev && prev.jobId === jobId ? prev.picks : {};
  if (picks[tab] === id) return;
  saveFloorSession({ jobId, picks: { ...picks, [tab]: id } });
}
